import * as THREE from 'three';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Canvas, applyProps, useFrame } from '@react-three/fiber';
import { PerformanceMonitor, ContactShadows, RandomizedLight, Environment, Lightformer, Float, useGLTF, OrbitControls } from '@react-three/drei';
import { LayerMaterial, Color, Depth } from 'lamina';
import { useSnapshot } from 'valtio';
import { useMediaQuery } from 'react-responsive';
import { state } from './store';
import './styles.css'; // Ensure your CSS is imported correctly
import { easing } from 'maath'

export function App() {
  const [degraded, degrade] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const interactionTimeout = useRef(null);
  const overlayRef = useRef();
  const canvasRef = useRef();
  
  const resetInteractionTimer = () => {
    clearTimeout(interactionTimeout.current);
    setShowOverlay(false);
    interactionTimeout.current = setTimeout(() => {
      setShowOverlay(true);
    }, 2000);
  };

  useEffect(() => {
    const resetTimerWithEvent = (event) => {
      if (overlayRef.current && overlayRef.current.contains(event.target)) {
        return; // If the event is from the overlay, ignore it
      }
      resetInteractionTimer();
    };

    document.addEventListener('mousemove', resetTimerWithEvent);
    document.addEventListener('touchstart', resetTimerWithEvent);

    resetInteractionTimer();

    return () => {
      document.removeEventListener('mousemove', resetTimerWithEvent);
      document.removeEventListener('touchstart', resetTimerWithEvent);
      clearTimeout(interactionTimeout.current);
    };
  }, []);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  return (
    <div className="viewer-container" ref={canvasRef}>
    <div className="canvas-container">

    {showOverlay && (
      <img src="Cursor-Gif_6.gif" alt="Inactivity Overlay" className="inactivity-overlay" />
    )}
    <Canvas className="canvas" shadows camera={{ position: [5, 0, 15], fov: 30 }} eventSource={document.getElementById('root')}>
      <spotLight position={[0, 15, 0]} angle={0.3} penumbra={1} intensity={2} />
      <ambientLight intensity={0.5} />
      <Porsche />
      <ContactShadows 
        position={[0, isMobile ? 0 : -0.16, 0]} 
        frames={100} 
        opacity={0.7} 
        blur={5}>
        <RandomizedLight amount={8} radius={10} ambient={0.5} position={[1, 5, -1]} />
    </ContactShadows>

      <PerformanceMonitor onDecline={() => degrade(true)} />
      <Environment frames={degraded ? 1 : Infinity} resolution={256} background blur={1}>
        <OrbitControls enableZoom={false} enablePan={false} autoRotate enableDamping={true} dampingFactor={0.1} />
        <Lightformers />
      </Environment>
    </Canvas>
    </div>
    <a href="https://calendly.com/vincent-119" target="_blank" rel="noopener noreferrer" className="viewer-demo-button">
        Book a Demo
      </a>
    </div>
  );
}

function Porsche(props) {
  const snap = useSnapshot(state);
  const { scene, nodes, materials } = useGLTF('/911-transformed.glb');
  const paintColorRef = useRef(new THREE.Color(snap.color));
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  useLayoutEffect(() => {
    Object.values(nodes).forEach((node) => {
      if (node.isMesh) {
        node.receiveShadow = true;
        node.castShadow = true;
      }
    });

    applyProps(materials.rubber, { color: '#222', roughness: 0.6 });
    applyProps(materials.window, { color: 'black', roughness: 0, clearcoat: 0.1, opacity: 0.7 });
    applyProps(materials.coat, { envMapIntensity: 4, roughness: 0.5, metalness: 1 });
    applyProps(materials.paint, { color: snap.color, envMapIntensity: 1, roughness: 0.45, metalness: 0.8 });
  }, [nodes, materials, snap.color]);

  useFrame((state, delta) => {
    const targetColor = new THREE.Color(snap.color);
    easing.dampC(paintColorRef.current, targetColor.toArray(), 0.25, delta);
    applyProps(materials.paint, { color: paintColorRef.current });

    // Adjust the position based on the scale
    const newPositionY = isMobile ? 0.63 : 1; // Adjust the Y position as needed
    scene.position.set(0, newPositionY, 0);
  });

  const scale = isMobile ? [0.8, 0.8, 0.8] : [1.8, 1.8, 1.8];

  return <primitive object={scene} {...props} scale={scale} />;
}


function Lightformers({ positions = [2, 0, 2, 0, 2, 0, 2, 0] }) {
  const group = useRef();
  useFrame((state, delta) => {
    if ((group.current.position.z += delta * 10) > 20) group.current.position.z = -60;
  });
  return (
    <>
      <Lightformer intensity={0.75} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} />
      <group rotation={[0, 0.5, 0]}>
        <group ref={group}>
          {positions.map((x, i) => (
            <Lightformer key={i} form="circle" intensity={2} rotation={[Math.PI / 2, 0, 0]} position={[x, 4, i * 4]} scale={[3, 1, 1]} />
          ))}
        </group>
      </group>
      <Lightformer intensity={4} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={[20, 0.1, 1]} />
      <Lightformer rotation-y={Math.PI / 2} position={[-5, -1, -1]} scale={[20, 0.5, 1]} />
      <Lightformer rotation-y={-Math.PI / 2} position={[10, 1, 0]} scale={[20, 1, 1]} />
      <Float speed={5} floatIntensity={2} rotationIntensity={2}>
        <Lightformer form="ring" color="red" intensity={1} scale={10} position={[-15, 4, -18]} target={[0, 0, 0]} />
      </Float>
      <mesh scale={100}>
        <sphereGeometry args={[1, 64, 64]} />
        <LayerMaterial side={THREE.BackSide}>
          <Color color="#444" alpha={1} mode="normal" />
          <Depth colorA="blue" colorB="black" alpha={0.5} mode="normal" near={0} far={300} origin={[100, 100, 100]} />
        </LayerMaterial>
      </mesh>
    </>
  );
}

